/**
 * Device Detection Utility
 * Detects device types and platform-specific features
 */

export interface DeviceInfo {
  isAndroid: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  platform: 'android' | 'desktop' | 'unknown';
  userAgent: string;
  isInMobileApp: boolean;
  bundleId: string | null;
  appFramework: {
    framework: 'react-native' | 'cordova' | 'capacitor' | 'expo' | 'webview' | 'pwa' | 'unknown';
    version?: string;
    platform?: string;
  };
}

/**
 * Detects if the current device is Android
 */
export const isAndroidDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return /android/.test(userAgent);
};

/**
 * Detects if the current device is mobile
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  return window.innerWidth < 768 ||
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(window.navigator.userAgent);
};

/**
 * Detects if the current device is tablet
 */
export const isTabletDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return /ipad|android(?!.*mobile)/.test(userAgent) ||
    (window.innerWidth >= 768 && window.innerWidth < 1024);
};

/**
 * Gets comprehensive device information
 */
export const getDeviceInfo = (): DeviceInfo => {
  if (typeof window === 'undefined') {
    return {
      isAndroid: false,
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      platform: 'unknown',
      userAgent: '',
      isInMobileApp: false,
      bundleId: null,
      appFramework: { framework: 'unknown' }
    };
  }

  const userAgent = window.navigator.userAgent;
  const isAndroid = isAndroidDevice();
  const isMobile = isMobileDevice();
  const isTablet = isTabletDevice();
  const isDesktop = !isMobile && !isTablet;
  const inMobileApp = isInMobileApp();
  const bundleId = getAppBundleId();
  const appFramework = getAppFramework();

  let platform: 'android' | 'desktop' | 'unknown' = 'unknown';
  if (isAndroid) platform = 'android';
  else if (isDesktop) platform = 'desktop';

  return {
    isAndroid,
    isMobile,
    isTablet,
    isDesktop,
    platform,
    userAgent,
    isInMobileApp: inMobileApp,
    bundleId,
    appFramework
  };
};

/**
 * Checks if the app is running in a WebView (like in a mobile app)
 */
export const isWebView = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();

  // Check for explicit WebView indicators
  const hasWebViewUA = /wv|webview/.test(userAgent);

  // Check for React Native WebView
  const hasReactNativeWebView = (window as any).ReactNativeWebView !== undefined;

  // Check for Cordova/PhoneGap
  const hasCordova = (window as any).cordova !== undefined;

  // Check for Capacitor
  const hasCapacitor = (window as any).Capacitor !== undefined;

  // Check for Expo WebView
  const hasExpoWebView = userAgent.includes('expo') || userAgent.includes('expo-webview');

  // Check for iOS WebView (many iOS WebViews don't include 'wv' in user agent)
  const isIOSWebView = /iphone|ipad|ipod/.test(userAgent) &&
    !/safari/.test(userAgent) &&
    !/chrome/.test(userAgent) &&
    !/firefox/.test(userAgent);

  // Check for Android WebView
  const isAndroidWebView = /android/.test(userAgent) && /wv/.test(userAgent);

  // Check for any bundle ID pattern in user agent (indicates mobile app)
  const hasBundleIdPattern = /com\.[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+/.test(userAgent);

  return hasWebViewUA || hasReactNativeWebView || hasCordova || hasCapacitor ||
    hasExpoWebView || isIOSWebView || isAndroidWebView || hasBundleIdPattern;
};

/**
 * Checks if the app is running in a PWA (Progressive Web App)
 */
export const isPWA = (): boolean => {
  if (typeof window === 'undefined') return false;

  return window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://');
};

/**
 * Checks if the app is running inside a mobile app (iOS or Android)
 */
export const isInMobileApp = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();

  // Check for React Native WebView
  const hasReactNativeWebView = (window as any).ReactNativeWebView !== undefined;

  // Check for Expo WebView
  const hasExpoWebView = userAgent.includes('expo') || userAgent.includes('expo-webview');

  // Check for iOS WebView
  const isIOSWebView = /wv|webview/.test(userAgent) && /iphone|ipad|ipod/.test(userAgent);

  // Check for Android WebView
  const isAndroidWebView = /wv|webview/.test(userAgent) && /android/.test(userAgent);

  // Check for Cordova/PhoneGap
  const hasCordova = (window as any).cordova !== undefined;

  // Check for Capacitor
  const hasCapacitor = (window as any).Capacitor !== undefined;

  // Check for any bundle ID pattern in user agent
  const hasBundleIdPattern = /com\.[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+/.test(userAgent);

  return hasReactNativeWebView || hasExpoWebView || isIOSWebView || isAndroidWebView ||
    hasCordova || hasCapacitor || hasBundleIdPattern;
};

/**
 * Gets the app bundle ID if running in a mobile app
 */
export const getAppBundleId = (): string | null => {
  if (typeof window === 'undefined') return null;

  const userAgent = window.navigator.userAgent;

  // Try to extract any bundle ID from user agent (generic pattern)
  const bundleIdMatches = userAgent.match(/com\.[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+/g);
  if (bundleIdMatches && bundleIdMatches.length > 0) {
    // Return the first (most likely primary) bundle ID found
    return bundleIdMatches[0];
  }

  // Check if we're in a React Native WebView and try to get bundle ID from window object
  if ((window as any).ReactNativeWebView !== undefined) {
    // Try to get bundle ID from React Native WebView if available
    const rnBundleId = (window as any).ReactNativeWebView?.getBundleId?.();
    if (rnBundleId) {
      return rnBundleId;
    }

    // Fallback: check if there's a bundle ID in the global object
    if ((window as any).__BUNDLE_ID__) {
      return (window as any).__BUNDLE_ID__;
    }
  }

  // Check for Cordova bundle ID
  if ((window as any).cordova) {
    const cordovaBundleId = (window as any).cordova?.platformId ||
      (window as any).cordova?.platform?.id;
    if (cordovaBundleId) {
      return cordovaBundleId;
    }
  }

  // Check for Capacitor bundle ID
  if ((window as any).Capacitor) {
    const capacitorBundleId = (window as any).Capacitor?.getPlatform?.() ||
      (window as any).Capacitor?.platform;
    if (capacitorBundleId) {
      return capacitorBundleId;
    }
  }

  // Check for any other common bundle ID patterns in user agent
  const genericBundleMatch = userAgent.match(/([a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+)/);
  if (genericBundleMatch && genericBundleMatch[1]) {
    return genericBundleMatch[1];
  }

  return null;
};

/**
 * Detects if the current device is iOS (for bundle ID detection only)
 */
export const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  const platform = window.navigator.platform?.toLowerCase() || '';

  // Check for iOS devices using multiple methods
  const isIOSUserAgent = /iphone|ipad|ipod/.test(userAgent);
  const isIOSPlatform = platform.includes('iphone') || platform.includes('ipad') || platform.includes('ipod');
  const isMacWithTouch = platform === 'macintel' && window.navigator.maxTouchPoints > 1;

  // Additional iOS detection methods
  const isIOSStandalone = (window.navigator as any).standalone === true;
  const isIOSWebView = /wv|webview/.test(userAgent) && (isIOSUserAgent || isIOSPlatform);

  // Check for iOS Safari specifically
  const isIOSSafari = /safari/.test(userAgent) && !/chrome|chromium|edge|firefox|opera/.test(userAgent) &&
    (isIOSUserAgent || isIOSPlatform);

  // Check for iOS in WebView (React Native, Cordova, etc.)
  const isIOSInWebView = (window as any).ReactNativeWebView !== undefined &&
    (isIOSUserAgent || isIOSPlatform);

  // Check for iOS PWA
  const isIOSPWA = window.matchMedia('(display-mode: standalone)').matches &&
    (isIOSUserAgent || isIOSPlatform);

  const result = isIOSUserAgent || isIOSPlatform || isMacWithTouch || isIOSStandalone ||
    isIOSWebView || isIOSSafari || isIOSInWebView || isIOSPWA;

  return result;
};

/**
 * Gets information about the mobile app framework being used
 */
export const getAppFramework = (): {
  framework: 'react-native' | 'cordova' | 'capacitor' | 'expo' | 'webview' | 'pwa' | 'unknown';
  version?: string;
  platform?: string;
} => {
  if (typeof window === 'undefined') {
    return { framework: 'unknown' };
  }

  const userAgent = window.navigator.userAgent;

  // Check for React Native
  if ((window as any).ReactNativeWebView !== undefined) {
    return {
      framework: 'react-native',
      platform: (window as any).ReactNativeWebView?.platform || 'unknown'
    };
  }

  // Check for Expo
  if (userAgent.includes('expo') || userAgent.includes('expo-webview')) {
    const expoVersion = userAgent.match(/expo\/([0-9.]+)/)?.[1];
    return {
      framework: 'expo',
      version: expoVersion
    };
  }

  // Check for Cordova
  if ((window as any).cordova !== undefined) {
    return {
      framework: 'cordova',
      version: (window as any).cordova?.version,
      platform: (window as any).cordova?.platformId
    };
  }

  // Check for Capacitor
  if ((window as any).Capacitor !== undefined) {
    return {
      framework: 'capacitor',
      version: (window as any).Capacitor?.version,
      platform: (window as any).Capacitor?.getPlatform?.()
    };
  }

  // Check for PWA
  if (window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true) {
    return { framework: 'pwa' };
  }

  // Check for WebView
  if (/wv|webview/.test(userAgent.toLowerCase())) {
    return { framework: 'webview' };
  }

  return { framework: 'unknown' };
};

/**
 * Checks if we should show iOS WebView alert
 */
export const shouldShowIOSWebViewAlert = (): boolean => {
  if (typeof window === 'undefined') return false;

  const isIOS = isIOSDevice();

  // If not iOS, don't show alert
  if (!isIOS) return false;

  const userAgent = window.navigator.userAgent.toLowerCase();

  // Check for various iOS WebView scenarios
  const hasReactNativeWebView = (window as any).ReactNativeWebView !== undefined;
  const hasCordova = (window as any).cordova !== undefined;
  const hasCapacitor = (window as any).Capacitor !== undefined;
  const hasExpoWebView = userAgent.includes('expo') || userAgent.includes('expo-webview');
  const hasBundleIdPattern = /com\.[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+/.test(userAgent);

  // Check for iOS WebView indicators (many don't include 'wv' in user agent)
  const isIOSWebView = /iphone|ipad|ipod/.test(userAgent) &&
    !/safari/.test(userAgent) &&
    !/chrome/.test(userAgent) &&
    !/firefox/.test(userAgent);

  // Check for explicit WebView indicators
  const hasWebViewUA = /wv|webview/.test(userAgent);

  // Check if it's a PWA on iOS (should also show alert)
  const isIOSPWA = window.matchMedia('(display-mode: standalone)').matches && isIOS;

  // Show alert if any of these conditions are true
  return hasReactNativeWebView || hasCordova || hasCapacitor || hasExpoWebView ||
    isIOSWebView || hasWebViewUA || hasBundleIdPattern || isIOSPWA;
};

/**
 * Shows iOS WebView alert popup
 */
export const showIOSWebViewAlert = (): void => {
  if (!shouldShowIOSWebViewAlert()) return;

  const bundleId = getAppBundleId();
  const appFramework = getAppFramework();

  const alertMessage = `iOS WebView Detected!

Framework: ${appFramework.framework}
${appFramework.version ? `Version: ${appFramework.version}` : ''}
${bundleId ? `Bundle ID: ${bundleId}` : 'Bundle ID: Not detected'}

This app is running inside an iOS WebView.`;

  // Use browser alert for now (can be replaced with custom modal)
  alert(alertMessage);
};

/**
 * Gets iOS WebView alert data for custom components
 */
export const getIOSWebViewAlertData = (): {
  shouldShow: boolean;
  bundleId: string | null;
  framework: string;
  version?: string;
} => {
  const shouldShow = shouldShowIOSWebViewAlert();
  const bundleId = getAppBundleId();
  const appFramework = getAppFramework();

  return {
    shouldShow,
    bundleId,
    framework: appFramework.framework,
    version: appFramework.version
  };
};

/**
 * Gets detailed iOS WebView detection information for debugging
 */
export const getIOSWebViewDetectionDetails = (): {
  isIOS: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  standalone: boolean;
  displayMode: string;
  reactNativeWebView: boolean;
  cordova: boolean;
  capacitor: boolean;
  expoWebView: boolean;
  bundleIdPattern: boolean;
  explicitWebView: boolean;
  isIOSWebView: boolean;
  isIOSPWA: boolean;
  shouldShowAlert: boolean;
} => {
  if (typeof window === 'undefined') {
    return {
      isIOS: false,
      userAgent: '',
      platform: '',
      maxTouchPoints: 0,
      standalone: false,
      displayMode: 'unknown',
      reactNativeWebView: false,
      cordova: false,
      capacitor: false,
      expoWebView: false,
      bundleIdPattern: false,
      explicitWebView: false,
      isIOSWebView: false,
      isIOSPWA: false,
      shouldShowAlert: false
    };
  }

  const isIOS = isIOSDevice();
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform || '';
  const maxTouchPoints = window.navigator.maxTouchPoints || 0;
  const standalone = (window.navigator as any).standalone === true;
  const displayMode = window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser';

  const reactNativeWebView = (window as any).ReactNativeWebView !== undefined;
  const cordova = (window as any).cordova !== undefined;
  const capacitor = (window as any).Capacitor !== undefined;
  const expoWebView = userAgent.toLowerCase().includes('expo') || userAgent.toLowerCase().includes('expo-webview');
  const bundleIdPattern = /com\.[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+/.test(userAgent);
  const explicitWebView = /wv|webview/.test(userAgent.toLowerCase());

  const isIOSWebView = /iphone|ipad|ipod/.test(userAgent.toLowerCase()) &&
    !/safari/.test(userAgent.toLowerCase()) &&
    !/chrome/.test(userAgent.toLowerCase()) &&
    !/firefox/.test(userAgent.toLowerCase());

  const isIOSPWA = window.matchMedia('(display-mode: standalone)').matches && isIOS;
  const shouldShowAlert = shouldShowIOSWebViewAlert();

  return {
    isIOS,
    userAgent,
    platform,
    maxTouchPoints,
    standalone,
    displayMode,
    reactNativeWebView,
    cordova,
    capacitor,
    expoWebView,
    bundleIdPattern,
    explicitWebView,
    isIOSWebView,
    isIOSPWA,
    shouldShowAlert
  };
};

